<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAutoCurtailmentManagement } from '@/composables/eos/auto-curtailment-management'
import { useSites } from '@/composables/core/sites'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import type { RealTimeCurtailmentDecisionAttributes } from '@/types/eos/AutoCurtailmentManagement'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import {
  formatCurrency,
  formatDollarPerKWh,
  formatDollarPerMWh,
  formatMWh,
  formatPercentage,
  formatTemperature,
  formatTimeRange,
} from '@/util/formatters'
import ManageAutoCurtailmentDialog from '@/components/eos/auto-curtailment/ManageAutoCurtailmentDialog.vue'
import { useOIDCStore } from '@/stores/oidc.store'
import { useRouter } from 'vue-router'
import { useForemanApiKeys } from '@/composables/core/foreman-api-keys'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
const router = useRouter()

const { isManagerUser } = useOIDCStore()

const selectedSite = ref<string>()
const sites = ref<OptionType[]>([])

const { getApiKeyQuery } = useForemanApiKeys()
const { getAllSitesQuery } = useSites()
const { data: sitesData, isLoading } = getAllSitesQuery()
const { data: apiKeyData } = getApiKeyQuery()

// Transform sites data when it's available
const transformedSites = computed(
  () =>
    sitesData.value?.map((site) => ({
      label: site.site_name,
      value: site.site_id,
    })) || []
)

// Set first site as default when sites data is loaded
watch(
  transformedSites,
  (newSites) => {
    if (newSites.length > 0 && !selectedSite.value) {
      selectedSite.value = newSites[0].value
      sites.value = newSites
    }
  },
  { immediate: true }
)

const { getRealTimeCurtailmentDecisionAttributesQuery, deleteRealTimeCurtailmentDecisionAttributesMutation } =
  useAutoCurtailmentManagement()

const { mutateAsync: deleteMutateAsync, isPending: isDeleting } = deleteRealTimeCurtailmentDecisionAttributesMutation

const computedSelectedSite = computed(() => selectedSite.value)
const {
  data: attributes,
  isLoading: isLoadingAttributes,
  isError,
  refetch,
} = getRealTimeCurtailmentDecisionAttributesQuery(computedSelectedSite)

interface CardConfig {
  label: string
  value: keyof RealTimeCurtailmentDecisionAttributes | 'custom_curtailment_window'
  formatter?: (value: any) => string | undefined
  description?: string
}

// Group cards by logical sections based on the flow diagram
const siteConfigurationCards: CardConfig[] = [
  {
    label: 'ISO',
    value: 'iso',
    description: 'Independent System Operator for market data',
  },
  {
    label: 'NODE',
    value: 'node',
    description: 'Grid node identifier for LMP data',
  },
  {
    label: 'DEFAULT LOAD',
    value: 'default_site_load_mwh',
    formatter: formatMWh,
    description: 'Energy amount in MW belonging to miners that the site is supposed to have',
  },
  {
    label: 'CUSTOM CURTAILMENT HOURS',
    value: 'custom_curtailment_window',
    formatter: (value: string) => value,
    description: 'Custom time window for curtailment operations',
  },
  {
    label: 'TEMPERATURE THRESHOLD',
    value: 'temperature_threshold_in_fahrenheit',
    formatter: formatTemperature,
    description: 'If curtailing the site outside a custom time window, the temperature must be above this threshold',
  },
]

const costsAndFeesCards: CardConfig[] = [
  {
    label: 'DEFAULT LMP',
    value: 'default_lmp_value',
    formatter: formatDollarPerMWh,
    description: 'Default LMP value used to fill empty intervals',
  },
  {
    label: 'SCHEDULING FEE',
    value: 'iso_scheduling_fee_percentage',
    formatter: (value: number) => formatPercentage(value, 1),
    description: 'ISO scheduling fee percentage',
  },
  {
    label: 'TAX',
    value: 'iso_tax_percentage',
    formatter: (value: number) => formatPercentage(value, 2),
    description: 'Tax percentage applied to transactions',
  },
  {
    label: 'ADDER VALUE',
    value: 'iso_adder_value_per_kwh',
    formatter: formatDollarPerKWh,
    description: 'Additional cost per kWh',
  },
  {
    label: 'REVENUE FROM CUSTOMERS',
    value: 'expected_revenue_per_mwh',
    formatter: formatDollarPerMWh,
    description: 'Expected revenue per MWh from customers',
  },
]

const dayAheadPricingCards: CardConfig[] = [
  {
    label: 'PROFIT THRESHOLD',
    value: 'curtailment_profit_threshold',
    formatter: formatCurrency,
    description: 'Minimum profit threshold to trigger curtailment',
  },
]

const realTimePricingCards: CardConfig[] = [
  {
    label: 'LMP SPIKE THRESHOLD',
    value: 'lmp_spike_threshold',
    formatter: formatCurrency,
    description: 'LMP spike threshold for monitoring',
  },
  {
    label: 'CONSECUTIVE LMP SPIKES',
    value: 'number_of_consecutive_lmp_spikes',
    formatter: (value: string) => `${value}`,
    description: 'Number of consecutive LMP spikes to trigger a curtailment',
  },
  {
    label: 'LOSS THRESHOLD',
    value: 'curtailment_loss_threshold',
    formatter: formatCurrency,
    description: 'Maximum loss threshold before curtailment is triggered',
  },
]

const showManageDialog = ref<boolean>(false)
const dialogMode = ref<'create' | 'manage'>('manage')

const handleCreate = () => {
  dialogMode.value = 'create'
  showManageDialog.value = true
}

const handleManage = () => {
  dialogMode.value = 'manage'
  showManageDialog.value = true
}

const handleDelete = async () => {
  if (!selectedSite.value) return

  await DeleteConfirmationModal(
    'Warning',
    'Are you sure you want to delete the auto curtailment settings for this site?',
    selectedSite.value,
    deleteMutateAsync,
    undefined,
    'Yes'
  )
}

const getCustomCurtailmentWindowValue = computed(() => {
  if (!attributes.value?.curtail_on_custom_time_window) return '-'

  const start = attributes.value.custom_curtailment_window_start_hour
  const end = attributes.value.custom_curtailment_window_end_hour

  if (start == null || end == null) return '-'

  // Convert hours to HH:mm format
  const formatHour = (hour: number) => {
    const hours = Math.floor(hour)
    const minutes = Math.round((hour - hours) * 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  return formatTimeRange(`${formatHour(start)}-${formatHour(end)}`)
})

const getCardValue = (card: CardConfig) => {
  if (card.value === 'custom_curtailment_window') {
    return getCustomCurtailmentWindowValue.value
  }

  const value = attributes.value?.[card.value]
  if (value == null) return '-'

  // Only try to parse numeric values for number formatters
  if (typeof value === 'string' && card.formatter && card.formatter.length === 1) {
    const numericValue = parseFloat(value)
    if (!isNaN(numericValue)) {
      const formattedValue = card.formatter(numericValue)
      return formattedValue ?? '-'
    }
  }

  if (card.formatter) {
    const formattedValue = card.formatter(value)
    return formattedValue ?? '-'
  }

  return value.toString()
}
</script>

<template>
  <ViewLayout>
    <template v-if="apiKeyData">
      <!-- Controls Row -->
      <div class="auto-curtailment-management__controls">
        <el-select
          v-model="selectedSite"
          placeholder="Select Site"
          class="auto-curtailment-management__site-select"
          :loading="isLoading"
        >
          <el-option v-for="site in sites" :key="site.value" :label="site.label" :value="site.value" />
        </el-select>

        <div class="auto-curtailment-management__buttons">
          <template v-if="attributes">
            <el-button :disabled="isError || isLoadingAttributes" @click="handleManage">Manage</el-button>
            <el-button
              :disabled="isError || isLoadingAttributes"
              type="danger"
              :loading="isDeleting"
              @click="handleDelete"
            >
              Delete
            </el-button>
          </template>
          <template v-else>
            <el-button type="primary" @click="handleCreate">Create</el-button>
          </template>
        </div>
      </div>

      <!-- Warning when automatic curtailment analysis is off -->
      <el-alert
        v-if="attributes?.disable_rt_curtailments"
        type="warning"
        :closable="false"
        show-icon
        class="auto-curtailment-management__warning"
      >
        <template #title>
          <strong>Automatic curtailment analysis is OFF</strong>
        </template>
        Real-time curtailment analysis is disabled for this site. Recommendations and automatic analysis will not run
        until it is turned back on in Manage settings.
      </el-alert>

      <!-- Content Row -->
      <div class="auto-curtailment-management__content">
        <!-- Site Configuration Section -->
        <div class="auto-curtailment-management__section">
          <h3 class="section-title">Site Configuration</h3>
          <p class="section-description">General site settings</p>
          <div class="auto-curtailment-management__cards">
            <el-card v-for="card in siteConfigurationCards" :key="card.label" class="auto-curtailment-management__card">
              <el-skeleton v-if="isLoadingAttributes || isLoading" :rows="1" animated />
              <template v-else>
                <el-text class="card-label" type="info">{{ card.label }}</el-text>
                <el-text class="card-value">
                  {{ getCardValue(card) }}
                </el-text>
                <el-text v-if="card.description" class="card-description" type="info">
                  {{ card.description }}
                </el-text>
              </template>
            </el-card>
          </div>
        </div>

        <!-- Costs and Fees Section -->
        <div class="auto-curtailment-management__section">
          <h3 class="section-title">Costs and Fees</h3>
          <p class="section-description">
            Market pricing, operational costs, and revenue parameters used in curtailment calculations
          </p>
          <div class="auto-curtailment-management__cards">
            <el-card v-for="card in costsAndFeesCards" :key="card.label" class="auto-curtailment-management__card">
              <el-skeleton v-if="isLoadingAttributes || isLoading" :rows="1" animated />
              <template v-else>
                <el-text class="card-label" type="info">{{ card.label }}</el-text>
                <el-text class="card-value">
                  {{ getCardValue(card) }}
                </el-text>
                <el-text v-if="card.description" class="card-description" type="info">
                  {{ card.description }}
                </el-text>
              </template>
            </el-card>
          </div>
        </div>

        <!-- Day Ahead Pricing Exposure Logic Section -->
        <div class="auto-curtailment-management__section">
          <h3 class="section-title">Day Ahead Pricing Exposure</h3>
          <p class="section-description">Parameters for Day Ahead pricing exposure logic and profit calculations</p>
          <div class="auto-curtailment-management__cards">
            <el-card v-for="card in dayAheadPricingCards" :key="card.label" class="auto-curtailment-management__card">
              <el-skeleton v-if="isLoadingAttributes || isLoading" :rows="1" animated />
              <template v-else>
                <el-text class="card-label" type="info">{{ card.label }}</el-text>
                <el-text class="card-value">
                  {{ getCardValue(card) }}
                </el-text>
                <el-text v-if="card.description" class="card-description" type="info">
                  {{ card.description }}
                </el-text>
              </template>
            </el-card>
          </div>
        </div>

        <!-- Real Time Pricing Exposure Logic Section -->
        <div class="auto-curtailment-management__section">
          <h3 class="section-title">Real Time Pricing Exposure Logic</h3>
          <p class="section-description">Parameters for Real Time pricing exposure logic</p>
          <div class="auto-curtailment-management__cards">
            <el-card v-for="card in realTimePricingCards" :key="card.label" class="auto-curtailment-management__card">
              <el-skeleton v-if="isLoadingAttributes || isLoading" :rows="1" animated />
              <template v-else>
                <el-text class="card-label" type="info">{{ card.label }}</el-text>
                <el-text class="card-value">
                  {{ getCardValue(card) }}
                </el-text>
                <el-text v-if="card.description" class="card-description" type="info">
                  {{ card.description }}
                </el-text>
              </template>
            </el-card>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <RlEmptyDataset
        title="Foreman API key not found"
        subtitle="You need to configure a Foreman API key to start using this feature"
      >
        <el-button
          v-if="isManagerUser"
          type="primary"
          @click="() => router.push({ name: 'core-foreman-configuration' })"
          >Configure</el-button
        >
      </RlEmptyDataset>
    </template>

    <ManageAutoCurtailmentDialog
      v-model="showManageDialog"
      :site="selectedSite"
      :attributes="attributes"
      :mode="dialogMode"
      @update="refetch"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
.auto-curtailment-management {
  &__title-row {
    margin-bottom: var(--spacing-spacing-lg);
  }

  &__title {
    margin: 0;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: var(--spacing-spacing-md);

    .auto-curtailment-management__buttons {
      margin-left: auto;
      display: flex;
      gap: var(--spacing-spacing-sm);
    }
  }

  &__site-select {
    width: 260px;
  }

  &__warning {
    margin-top: var(--spacing-spacing-md);
  }

  &__content {
    margin-top: var(--spacing-spacing-lg);
  }

  &__section {
    margin-bottom: var(--spacing-spacing-xl);

    .section-title {
      margin: 0 0 var(--spacing-spacing-sm) 0;
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      color: var(--text-primary);
    }

    .section-description {
      margin: 0 0 var(--spacing-spacing-md) 0;
      color: var(--text-secondary);
      font-size: var(--font-size-sm);
    }
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--spacing-spacing-md);
  }

  &__card {
    border: none;

    .card-label {
      display: block;
      margin-bottom: var(--spacing-spacing-sm);
      font-weight: var(--font-weight-medium);
    }

    .card-value {
      display: block;
      font-size: 24px;
      line-height: 32px;
      font-weight: 700;
      color: var(--text-primary);
      margin-bottom: var(--spacing-spacing-xs);
    }

    .card-description {
      display: block;
      font-size: var(--font-size-xs);
      line-height: 1.4;
      opacity: 0.8;
    }
  }
}
</style>
